import { useEffect, useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { numero } from "../lib/admin";
import { horaComArtigo } from "../lib/horaNoTexto";
import {
  vazioDoPeriodo,
  AINDA_SEM_AVALIACAO,
  buscaSoDoPeriodo,
  chaveDoPedido,
  chaveDoPeriodo,
  lerComparacao,
  lerSeries,
  metaLegivel,
  notaLegivel,
  pctLegivel,
  quandoLegivel,
  rotuloDoPeriodo,
  useLeitura,
  type Comparacao,
  type Granularidade,
  type LinhaDaFerramenta,
  type PedidoDePeriodo,
  type Periodo,
  type Series,
} from "../lib/avaliacoes";
import { GraficoDeLinhas, type SerieDoGrafico } from "./AdminAvaliacoesGraficos";
import { AvisoDaLeitura, ChaveDaSerie, LinhaDoPeriodo, SeloDaSituacao, fraseSolta, type Posicao } from "./AdminAvaliacoesUi";

/**
 * Admin › Avaliações › Comparar (000327/000328): até quatro ferramentas no
 * tempo e lado a lado.
 *
 * É o Comparar do painel antigo (`feedbacks-cf`), com o conteúdo de lá — a
 * evolução da média de cada uma e a tabela de números com a melhor e a pior de
 * cada linha destacadas — e quatro diferenças que são decisão:
 *
 *   · o período é o do painel (a barra acima das sub-abas), e o eixo do tempo
 *     segue a granularidade que o BANCO escolheu para ele: por hora em "Hoje",
 *     por dia até 31 dias, por semana (começando na segunda) até 190 dias, por
 *     mês acima disso. Lá o Comparar era sempre de todo o tempo e sempre por
 *     semana; a primeira versão daqui (000327) era sempre por mês — e em 30 dias
 *     isso dava um ou dois pontos por ferramenta, que não é evolução nenhuma;
 *   · o gráfico é o `GraficoDeLinhas` das outras sub-abas (o mesmo da página de
 *     cada ferramenta), com a meta tracejada e o ponto VAZADO onde menos de
 *     cinco avaliações sustentam a média: um dia com uma avaliação só parece uma
 *     queda e é uma pessoa;
 *   · o "NPS" da tabela de lá era sobre a nota de SATISFAÇÃO (9 ou mais contra 6
 *     ou menos), e o do Dashboard de lá, sobre a MÉDIA — duas contas com o mesmo
 *     nome. Aqui é uma só, o NPS ADAPTADO do banco (sobre a média), com esse
 *     nome, porque no hub "NPS" é a conta das pesquisas;
 *   · sem escolha na URL entram as DUAS mais avaliadas do período, como lá, e a
 *     escolha vai para a URL na hora (ver `Comparar`).
 *
 * ZERO conta aqui. As médias, as faixas contadas, o NPS adaptado, a situação da
 * meta e os pontos de cada balde vêm prontos do banco (`av_admin_comparacao` e
 * `av_admin_series`). A tela escolhe, ordena, compara os números QUE MOSTRA para
 * destacar a melhor e a pior, e desenha.
 *
 * DUAS leituras, de propósito: a da lista (os chips e o "Lado a lado") traz
 * todas as ferramentas numa ida só, e a do gráfico traz só as escolhidas. Tirar
 * ou pôr uma ferramenta relê só o gráfico — os chips e a tabela respondem na
 * hora, porque já têm a linha de todas.
 *
 * A escolha mora na URL (`?comparar=a,,c`), com as POSIÇÕES: a cor e a forma de
 * cada série são da posição, e tirar a segunda ferramenta deixa a vaga dela
 * vazia em vez de empurrar as outras. Sem isso, desmarcar uma ferramenta
 * repintaria as que ficaram, e quem estava lendo "a laranja" perderia o fio.
 */

const MAXIMO = 4;

/**
 * Sem escolha, as duas mais avaliadas — como o painel antigo, e não as quatro da
 * 000327: com o eixo por DIA, quatro linhas de 30 pontos cada viram um novelo, e
 * comparar começa por duas. Quem quiser mais acrescenta.
 */
const PADRAO = 2;

type Escolhida = { p: Posicao; linha: LinhaDaFerramenta };

/**
 * A URL guarda POSIÇÕES: a vaga vazia é a de quem saiu. O que não é ferramenta
 * do painel, o repetido e o que passa da quarta vaga viram vaga vazia — e o
 * slug é comparado em minúsculas, como o banco o compara.
 */
function posicoesDaBusca(valor: string, validos: ReadonlySet<string>): string[] {
  const partes = valor.split(",");
  const vistos = new Set<string>();
  return Array.from({ length: MAXIMO }, (_, i) => {
    const s = (partes[i] ?? "").trim().toLowerCase();
    if (!s || !validos.has(s) || vistos.has(s)) return "";
    vistos.add(s);
    return s;
  });
}

/** As posições de volta para a URL, sem as vagas vazias do FIM ("a,,c", e não "a,,c,"). */
function buscaDasPosicoes(posicoes: string[]): string {
  let fim = posicoes.length;
  while (fim > 0 && !posicoes[fim - 1]) fim--;
  return posicoes.slice(0, fim).join(",");
}

/** Os nomes numa frase, entre aspas angulares: "CST e ClassTrib" já tem um "e" dentro, e sem as aspas a lista ficaria ambígua. */
function emFrase(nomes: string[]): string {
  const q = nomes.map((n) => `«${n}»`);
  if (q.length <= 1) return q[0] ?? "";
  return `${q.slice(0, -1).join(", ")} e ${q[q.length - 1]}`;
}

const POR_GRANULARIDADE: Record<Granularidade, string> = {
  hora: "por hora",
  dia: "por dia",
  semana: "por semana",
  mes: "por mês",
};

/** O que cada granularidade quer dizer para quem lê — o recorte é do banco, e a frase só o conta. */
const FRASE_DA_GRANULARIDADE: Record<Granularidade, string> = {
  hora: "Por hora, no fuso de São Paulo.",
  dia: "Por dia.",
  semana: "Por semana, começando na segunda-feira — a primeira e a última podem vir recortadas pelo período.",
  mes: "Por mês — o primeiro e o último podem vir recortados pelo período.",
};

const UNIDADE: Record<Granularidade, string> = { hora: "hora", dia: "dia", semana: "semana", mes: "mês" };

export default function AdminAvaliacoesComparacao({ pedido }: { pedido: PedidoDePeriodo }) {
  const { estado } = useLeitura((sinal) => lerComparacao(pedido, sinal), chaveDoPedido(pedido));
  if (estado.fase !== "pronto") return <AvisoDaLeitura estado={estado} />;
  return <Comparar dados={estado.dados} atualizando={estado.atualizando} pedido={pedido} />;
}

function Comparar({ dados, atualizando, pedido }: { dados: Comparacao; atualizando: boolean; pedido: PedidoDePeriodo }) {
  const [busca, setBusca] = useSearchParams();
  const porSlug = useMemo(() => new Map(dados.ferramentas.map((f) => [f.slug, f])), [dados.ferramentas]);
  const validos = useMemo(() => new Set(porSlug.keys()), [porSlug]);

  const padrao = useMemo(
    () =>
      dados.ferramentas
        .filter((f) => f.avaliacoes > 0)
        .sort((a, b) => b.avaliacoes - a.avaliacoes || a.nome.localeCompare(b.nome, "pt-BR") || a.slug.localeCompare(b.slug))
        .slice(0, PADRAO)
        .map((f) => f.slug)
        .join(","),
    [dados.ferramentas],
  );

  // O padrão vai para a URL na hora — e só o padrão DESTE período, já lido:
  // senão trocar o período trocaria as ferramentas, ou as cores, porque "as
  // duas mais avaliadas" muda de uma janela para outra. Medido em 29/09, no
  // calendário de São Paulo: em 30 dias são a Central Inteligente IBS-CBS (235)
  // e o CST e ClassTrib (184); em 7 dias, as MESMAS duas na ordem inversa (47 e
  // 41) — e a primeira posição é a do azul.
  //
  // Um período sem avaliação NÃO grava a escolha vazia: ela seria uma escolha
  // que ninguém fez, e congelaria a tela vazia quando o período mudasse para um
  // com avaliações. Quem esvazia a comparação à mão grava `comparar=` — e aí a
  // escolha vazia é dela, e fica.
  const param = busca.get("comparar");
  const dosDadosDoPedido = chaveDoPeriodo(dados.periodo) === chaveDoPedido(pedido);
  useEffect(() => {
    if (param !== null || atualizando || !dosDadosDoPedido || !padrao) return;
    const nova = new URLSearchParams(busca);
    nova.set("comparar", padrao);
    setBusca(nova, { replace: true });
  }, [param, atualizando, dosDadosDoPedido, padrao, busca, setBusca]);

  const posicoes = useMemo(() => posicoesDaBusca(param ?? padrao, validos), [param, padrao, validos]);
  const escolhidas = useMemo(
    () =>
      posicoes.flatMap((slug, i): Escolhida[] => {
        const linha = slug ? porSlug.get(slug) : undefined;
        return linha ? [{ p: (i + 1) as Posicao, linha }] : [];
      }),
    [posicoes, porSlug],
  );
  const cheio = escolhidas.length >= MAXIMO;

  function alternar(slug: string) {
    const atuais = [...posicoes];
    const i = atuais.indexOf(slug);
    if (i >= 0) atuais[i] = "";
    else {
      const livre = atuais.indexOf("");
      if (livre < 0) return;
      atuais[livre] = slug;
    }
    const nova = new URLSearchParams(busca);
    nova.set("comparar", buscaDasPosicoes(atuais));
    setBusca(nova, { replace: true });
  }

  // Os chips: as que têm avaliação no período, e as escolhidas mesmo sem —
  // senão uma escolhida sem avaliação ficaria na tabela sem ter como ser tirada.
  // Pelo nome: é como se procura uma ferramenta numa fileira de doze.
  const opcoes = useMemo(
    () =>
      dados.ferramentas
        .filter((f) => f.avaliacoes > 0 || posicoes.includes(f.slug))
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR") || a.slug.localeCompare(b.slug)),
    [dados.ferramentas, posicoes],
  );

  const periodoNaBusca = buscaSoDoPeriodo(pedido);
  // A releitura da LISTA esmaece o que vem dela (os chips e a tabela), e não a
  // raiz: o gráfico tem a leitura dele e o esmaecimento dele, e os dois um
  // dentro do outro multiplicariam a opacidade.
  const esmaecido = `transition-opacity ${atualizando ? "opacity-60" : ""}`;

  return (
    <div
      className="space-y-5"
      aria-busy={atualizando}
      data-aba-das-avaliacoes="comparar"
      data-periodo-dos-dados={chaveDoPeriodo(dados.periodo)}
      data-painel-pronto={!atualizando}
      data-escolhas={buscaDasPosicoes(posicoes)}
    >
      <LinhaDoPeriodo periodo={dados.periodo} atualizando={atualizando} />

      <section className={`surface p-5 sm:p-6 ${esmaecido}`} role="group" aria-labelledby="comparar-titulo-escolha">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 id="comparar-titulo-escolha" className="font-display text-lg font-bold text-titulo">
            Ferramentas na comparação
          </h2>
          <p className="text-xs font-semibold tabular-nums text-cinza" role="status" data-contagem-das-escolhidas={escolhidas.length}>
            {escolhidas.length} de {MAXIMO} escolhidas{cheio ? " — para trocar, tire uma" : ""}
          </p>
        </div>
        <p className="mt-1 max-w-3xl text-xs leading-relaxed text-cinza">
          Até quatro por vez: com mais, as linhas deixam de se distinguir. A cor e a forma acompanham a ferramenta — tirar
          uma não muda a cor das outras. Ao abrir, entram as duas mais avaliadas do período. O número ao lado de cada nome
          é o de avaliações de alunos no período.
        </p>
        {opcoes.length === 0 ? (
          <p className="mt-3 text-sm text-cinza" data-sem-ferramentas-no-periodo>
            {vazioDoPeriodo(
              dados.periodo,
              "Nenhuma ferramenta recebeu avaliação de aluno neste período. Escolha outro período acima.",
              "O hub ainda não tem avaliação deste período. Escolha outro período acima.",
            )}
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Escolher as ferramentas" data-escolha-de-ferramentas>
            {opcoes.map((f) => {
              const i = posicoes.indexOf(f.slug);
              const escolhida = i >= 0;
              const desligado = !escolhida && cheio;
              return (
                <button
                  key={f.slug}
                  type="button"
                  aria-pressed={escolhida}
                  disabled={desligado}
                  onClick={() => alternar(f.slug)}
                  data-escolher={f.slug}
                  data-posicao={escolhida ? i + 1 : undefined}
                  title={desligado ? "Já há quatro na comparação: tire uma para escolher esta." : undefined}
                  // `max-w-full` e `text-left`: no celular o nome mais longo
                  // ("Leitor Inteligente de Processos") quebra DENTRO do chip, em
                  // vez de empurrar a página para o lado.
                  className={`inline-flex min-h-10 max-w-full items-center gap-2 rounded-full border px-3.5 text-left text-sm font-semibold transition ${
                    escolhida
                      ? "border-acento bg-superficie text-tinta ring-1 ring-acento"
                      : desligado
                        ? "cursor-not-allowed border-borda bg-superficie-2 text-cinza"
                        : "border-borda bg-superficie text-tinta hover:border-acento"
                  }`}
                >
                  {escolhida && <ChaveDaSerie p={(i + 1) as Posicao} />}
                  {f.nome}
                  <span className="font-normal tabular-nums text-cinza">
                    {numero(f.avaliacoes)}
                    <span className="sr-only"> {f.avaliacoes === 1 ? "avaliação" : "avaliações"}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {escolhidas.length === 0 ? (
        <EvolucaoVazia periodo={dados.periodo} temOpcoes={opcoes.length > 0} />
      ) : (
        <EvolucaoDasEscolhidas escolhidas={escolhidas} pedido={pedido} periodoDaLista={dados.periodo} />
      )}

      {escolhidas.length > 0 && <LadoALado escolhidas={escolhidas} periodoNaBusca={periodoNaBusca} className={esmaecido} />}
    </div>
  );
}

// ── A evolução ──────────────────────────────────────────────────────────────

/**
 * O título e o recorte do tempo. O ponto vazado NÃO é explicado aqui: o
 * `GraficoDeLinhas` já escreve a nota dele, com o mesmo símbolo, e só quando há
 * ponto vazado na tela — a frase aqui a repetiria, e a diria também quando não
 * há nenhum.
 */
function CabecalhoDaEvolucao({ granularidade }: { granularidade: Granularidade }) {
  return (
    <>
      <h2 id="comparar-titulo-evolucao" className="font-display text-lg font-bold text-titulo">
        Evolução da média geral
      </h2>
      <p className="mt-1 max-w-3xl text-xs leading-relaxed text-cinza" data-granularidade-do-grafico={granularidade}>
        {FRASE_DA_GRANULARIDADE[granularidade]} Só entram as avaliações de alunos.
      </p>
    </>
  );
}

/** Sem escolhida não há o que ler: o bloco já está no estado final, e diz o que falta. */
function EvolucaoVazia({ periodo, temOpcoes }: { periodo: Periodo; temOpcoes: boolean }) {
  return (
    <section
      className="surface p-5 sm:p-6"
      role="group"
      aria-labelledby="comparar-titulo-evolucao"
      data-evolucao
      data-grafico-pronto="true"
      data-grafico-estado="vazio"
    >
      <CabecalhoDaEvolucao granularidade={periodo.granularidade} />
      <p className="mt-4 text-sm text-cinza" data-comparacao-vazia>
        {temOpcoes
          ? "Escolha ao menos uma ferramenta acima."
          : vazioDoPeriodo(
              periodo,
              "Nenhuma ferramenta recebeu avaliação de aluno neste período, e por isso não há o que comparar — escolha outro período acima.",
              "O hub ainda não tem avaliação deste período, e por isso não há o que comparar — escolha outro período acima.",
            )}
      </p>
    </section>
  );
}

/**
 * O `data-grafico-pronto` sai do estado da leitura, e só dele. Até 29/09 a
 * resposta viajava com a CHAVE do pedido que a trouxe, conferida aqui de novo:
 * `useLeitura` marcava "atualizando" num efeito, depois do primeiro quadro com a
 * chave nova, e nesse quadro o gráfico mostrava as séries da escolha anterior
 * sem estar marcado como tal. Desde que o estado é derivado no render, o
 * primeiro quadro já chega "atualizando", e a segunda conferência saiu — ela
 * não decidia mais nada.
 */
function EvolucaoDasEscolhidas({
  escolhidas,
  pedido,
  periodoDaLista,
}: {
  escolhidas: Escolhida[];
  pedido: PedidoDePeriodo;
  periodoDaLista: Periodo;
}) {
  // Na ordem das posições: é a ordem em que o banco devolve as séries, e a da cor.
  const cards = escolhidas.map((e) => e.linha.slug);
  const chave = `${chaveDoPedido(pedido)}|${cards.join(",")}`;
  const { estado } = useLeitura<Series>((sinal) => lerSeries(cards, pedido, sinal), chave);

  const dados = estado.fase === "pronto" ? estado.dados : null;
  const pronto = estado.fase === "pronto" ? !estado.atualizando : estado.fase !== "carregando";
  const fase = estado.fase === "carregando" ? "carregando" : estado.fase === "pronto" ? (pronto ? "pronto" : "atualizando") : "erro";
  const periodo = dados?.periodo ?? periodoDaLista;
  const g = periodo.granularidade;

  // As séries do gráfico, na posição ATUAL de cada ferramenta: a que saiu da
  // escolha some na hora, sem esperar a releitura, e a que entrou aparece quando
  // os pontos dela chegam. Os pontos são alinhados aos baldes pela CHAVE do
  // balde, e não pela posição no arranjo — o banco promete os dois alinhados, e
  // o alinhamento pela chave não depende da promessa.
  const posicaoDe = new Map(escolhidas.map((e) => [e.linha.slug, e.p] as const));
  const desenho = dados
    ? dados.series
        .flatMap((s) => {
          const p = posicaoDe.get(s.card);
          if (!p) return [];
          const porBalde = new Map(s.pontos.map((pt) => [pt.chave, pt]));
          const pontos = dados.baldes.map((b) => porBalde.get(b.chave));
          const serie: SerieDoGrafico = {
            id: s.card,
            rotulo: s.nome,
            p,
            valores: pontos.map((pt) => pt?.media_geral ?? null),
            contagens: pontos.map((pt) => pt?.avaliacoes ?? 0),
          };
          return [{ serie, meta: s.meta }];
        })
        .sort((a, b) => a.serie.p - b.serie.p)
    : [];

  const metas = [...new Set(desenho.flatMap((d) => (d.meta === null ? [] : [d.meta])))]
    .sort((a, b) => b - a)
    .map((valor) => ({ valor, rotulo: `meta ${metaLegivel(valor)}` }));
  // A frase das metas fala das linhas tracejadas, e sem ponto nenhum o gráfico
  // não desenha linha nenhuma — só a frase de "nenhuma avaliação". Aí ela
  // descreveria uma linha que não está na tela.
  const temPonto = desenho.some((d) => d.serie.valores.some((v) => v !== null));
  const metasEmFrase = temPonto ? fraseDasMetas(desenho.map((d) => ({ nome: d.serie.rotulo, meta: d.meta }))) : null;
  // Sem série desenhável (a escolha acabou de mudar e os pontos da nova ainda não
  // chegaram), a frase não pode terminar em "de ." — ela diz o que está havendo.
  const descricao = desenho.length
    ? `Média geral ${POR_GRANULARIDADE[g]} de ${emFrase(desenho.map((d) => d.serie.rotulo))}. ${rotuloDoPeriodo(periodo)}.`
    : "A evolução das ferramentas escolhidas está sendo carregada.";

  return (
    <section
      className={`surface p-5 sm:p-6 transition-opacity ${fase === "atualizando" ? "opacity-60" : ""}`}
      role="group"
      aria-labelledby="comparar-titulo-evolucao"
      aria-busy={!pronto}
      data-evolucao
      data-grafico-pronto={pronto}
      data-grafico-estado={fase}
      data-periodo-do-grafico={dados ? chaveDoPeriodo(dados.periodo) : undefined}
      data-series-do-grafico={dados ? dados.series.map((s) => s.card).join(",") : undefined}
    >
      <CabecalhoDaEvolucao granularidade={g} />

      {estado.fase === "carregando" && (
        // O lugar do gráfico, já do tamanho dele: o carregador da casa ocupa a
        // tela inteira, e trocá-lo pelo gráfico empurraria o "Lado a lado".
        <div
          className="mt-4 flex min-h-72 items-center justify-center rounded-xl border border-dashed border-borda text-sm text-cinza"
          role="status"
        >
          Carregando a evolução…
        </div>
      )}
      {(estado.fase === "erro" || estado.fase === "negado") && (
        // A frase já diz o que não veio ("a evolução não veio — …"): com o
        // prefixo de antes, a tela dizia "A evolução não veio: a evolução não
        // veio — tente recarregar a página".
        <p className="mt-4 text-sm text-erro-tinta" role="alert" data-grafico-erro>
          {fraseSolta(estado.detalhe)}
        </p>
      )}
      {dados && (
        <div className="mt-4">
          <GraficoDeLinhas
            baldes={dados.baldes}
            granularidade={g}
            series={desenho.map((d) => d.serie)}
            metas={metas}
            descricao={descricao}
            semPontos={
              dados.periodo.comparacao === "sem_dado_ainda"
                ? AINDA_SEM_AVALIACAO
                : "Nenhuma avaliação de aluno no período para as ferramentas escolhidas."
            }
            rotuloDaTabela={`Ver os números de cada ${UNIDADE[g]}`}
            dado="comparacao"
            horizonte={dados.periodo.horizonte}
          />
        </div>
      )}
      {dados && metasEmFrase && (
        <p className="mt-3 text-xs leading-relaxed text-cinza" data-frase-das-metas>
          {metasEmFrase}
        </p>
      )}
      {escolhidas.length === 1 && (
        <p className="mt-3 text-sm text-tinta" data-convite-a-comparar>
          Escolha outra ferramenta acima para ver as duas no mesmo gráfico e lado a lado.
        </p>
      )}
    </section>
  );
}

const QUANTAS = ["", "", "as duas", "as três", "as quatro"];

/**
 * De quem é cada linha tracejada. A meta não tem cor de série (ela é a régua,
 * não uma das linhas), e o rótulo na margem só cabe "meta 9,0" — com metas
 * diferentes, é esta frase que diz qual é de quem. Apresentação: os números são
 * os que o banco devolveu com a série.
 */
function fraseDasMetas(series: { nome: string; meta: number | null }[]): string | null {
  if (series.length === 0) return null;
  const grupos = new Map<number, string[]>();
  const semMeta: string[] = [];
  for (const s of series) {
    if (s.meta === null) semMeta.push(s.nome);
    else grupos.set(s.meta, [...(grupos.get(s.meta) ?? []), s.nome]);
  }
  const metas = [...grupos.entries()].sort((a, b) => b[0] - a[0]);
  if (metas.length === 0) {
    return series.length === 1
      ? "Esta ferramenta não tem meta, e por isso o gráfico não tem linha tracejada."
      : "Nenhuma das escolhidas tem meta, e por isso o gráfico não tem linha tracejada.";
  }
  const sem =
    semMeta.length === 0 ? "" : ` ${emFrase(semMeta)} ${semMeta.length === 1 ? "não tem meta" : "não têm meta"}.`;
  if (metas.length === 1) {
    const [valor, nomes] = metas[0];
    if (semMeta.length === 0) {
      return series.length === 1
        ? `A linha tracejada é a meta: ${metaLegivel(valor)}.`
        : `A linha tracejada é a meta: ${metaLegivel(valor)}, a mesma para ${QUANTAS[series.length]}.`;
    }
    return `A linha tracejada é a meta de ${emFrase(nomes)}: ${metaLegivel(valor)}.${sem}`;
  }
  const partes = metas.map(([valor, nomes]) => `${metaLegivel(valor)} de ${emFrase(nomes)}`);
  return `As linhas tracejadas são as metas: ${partes.join("; ")}.${sem}`;
}

// ── Lado a lado ─────────────────────────────────────────────────────────────

type Destaque = "melhor" | "pior";

type LinhaDoLado = {
  chave: string;
  rotulo: string;
  /** O que o número quer dizer, embaixo do rótulo. */
  explicacao?: string;
  valor: (l: LinhaDaFerramenta) => ReactNode;
  /**
   * O número que o destaque compara — o que a célula MOSTRA, já arredondado pelo
   * banco. A linha sem ele não tem melhor nem pior: meta, situação, volume e
   * data não são qualidade.
   */
  comparar?: (l: LinhaDaFerramenta) => number | null;
  /** Nas baixas, menor é melhor. */
  menorEMelhor?: boolean;
  forte?: boolean;
};

/** Sem avaliação no período, contagem de avaliação não mede nada: "—", e não "0". */
function semAvaliacao(l: LinhaDaFerramenta): boolean {
  return l.avaliacoes === 0;
}

/** O NPS adaptado vai de −100 a 100, com o sinal de menos de verdade (o leitor de tela o lê como "menos"). */
function npsLegivel(n: number | null): string {
  if (n === null) return "—";
  return n < 0 ? `−${Math.abs(n)}` : String(n);
}

/** O dia e a hora no fuso de São Paulo — o dia que o período usa. */
function ultimaLegivel(iso: string | null): ReactNode {
  if (!iso) return "—";
  const q = quandoLegivel(iso);
  return (
    <>
      {q.dia}
      <span className="block text-xs text-cinza">{horaComArtigo(q.hora)}</span>
    </>
  );
}

const LINHAS_DO_LADO: LinhaDoLado[] = [
  {
    chave: "media",
    rotulo: "Média geral",
    // De 0 a 10: o formulário do hub vai de 1 a 10, e o app antigo aceitava
    // zero — há avaliações importadas com nota 0 (a frase inteira está no
    // «Como se lê» da Visão geral e das Ferramentas).
    explicacao: "das três notas, de 0 a 10",
    valor: (l) => notaLegivel(l.media_geral),
    comparar: (l) => l.media_geral,
    forte: true,
  },
  { chave: "meta", rotulo: "Meta", valor: (l) => metaLegivel(l.meta) },
  { chave: "situacao", rotulo: "Situação", valor: (l) => <SeloDaSituacao linha={l} /> },
  { chave: "utilidade", rotulo: "Utilidade", valor: (l) => notaLegivel(l.utilidade), comparar: (l) => l.utilidade },
  { chave: "facilidade", rotulo: "Facilidade", valor: (l) => notaLegivel(l.facilidade), comparar: (l) => l.facilidade },
  { chave: "satisfacao", rotulo: "Satisfação", valor: (l) => notaLegivel(l.satisfacao), comparar: (l) => l.satisfacao },
  { chave: "avaliacoes", rotulo: "Avaliações de alunos", valor: (l) => numero(l.avaliacoes) },
  {
    chave: "satisfeitas",
    rotulo: "Satisfeitas",
    explicacao: "média 8 ou mais",
    valor: (l) => pctLegivel(l.satisfeitas_pct),
    comparar: (l) => l.satisfeitas_pct,
  },
  {
    chave: "nps",
    rotulo: "NPS adaptado",
    explicacao: "de −100 a 100",
    valor: (l) => npsLegivel(l.nps_adaptado),
    comparar: (l) => l.nps_adaptado,
  },
  {
    chave: "baixas",
    rotulo: "Baixas",
    explicacao: "média 5 ou menos",
    valor: (l) => (semAvaliacao(l) ? "—" : `${pctLegivel(l.baixas_pct)} (${numero(l.baixas)})`),
    comparar: (l) => l.baixas_pct,
    menorEMelhor: true,
  },
  { chave: "comentarios", rotulo: "Com comentário", valor: (l) => (semAvaliacao(l) ? "—" : numero(l.com_comentario)) },
  { chave: "ultima", rotulo: "Última avaliação", explicacao: "no período", valor: (l) => ultimaLegivel(l.ultima) },
];

/**
 * A melhor e a pior de UMA linha do lado a lado. Apresentação, como
 * `sentidoDaVariacao`: compara os números que a tela mostra, e não calcula
 * número nenhum. A regra é a do painel antigo — a célula igual à melhor é a
 * melhor, e num empate as empatadas levam a mesma marca —, com duas travas que
 * lá não havia:
 *
 *   · só entram as ferramentas COM avaliação no período. Lá, a escolhida sem
 *     avaliação entrava com tudo ZERO e virava "a pior" de toda linha — e nulo
 *     não é zero;
 *   · só com duas ou mais: uma sozinha não é melhor nem pior que ninguém.
 */
function destaquesDaLinha(
  linhas: LinhaDaFerramenta[],
  comparar: (l: LinhaDaFerramenta) => number | null,
  menorEMelhor: boolean,
): Map<string, Destaque> {
  const candidatas = linhas.flatMap((l) => {
    const v = l.avaliacoes > 0 ? comparar(l) : null;
    return v === null ? [] : [{ slug: l.slug, v }];
  });
  const marcas = new Map<string, Destaque>();
  const maior = Math.max(...candidatas.map((c) => c.v));
  const menor = Math.min(...candidatas.map((c) => c.v));
  // "Só com duas ou mais" e "só quando diferem" são UMA regra: uma sozinha é ao
  // mesmo tempo a maior e a menor. Por isso não há guarda de contagem — ela não
  // decidiria nada, e a sabotagem que a tirava passaria verde (a lição do CST,
  // 14/09). Sem candidata nenhuma, o laço abaixo não marca ninguém.
  if (maior === menor) return marcas;
  const melhor = menorEMelhor ? menor : maior;
  const pior = menorEMelhor ? maior : menor;
  for (const c of candidatas) {
    if (c.v === melhor) marcas.set(c.slug, "melhor");
    else if (c.v === pior) marcas.set(c.slug, "pior");
  }
  return marcas;
}

function LadoALado({
  escolhidas,
  periodoNaBusca,
  className,
}: {
  escolhidas: Escolhida[];
  periodoNaBusca: string;
  className: string;
}) {
  const linhas = escolhidas.map((e) => e.linha);
  return (
    <section className={`surface p-5 sm:p-6 ${className}`} role="group" aria-labelledby="comparar-titulo-lado" data-lado-a-lado>
      <h2 id="comparar-titulo-lado" className="font-display text-lg font-bold text-titulo">
        Lado a lado
      </h2>
      <p className="mt-1 max-w-3xl text-xs leading-relaxed text-cinza">
        Os números do período escolhido acima, só com as avaliações de alunos — a equipe fica fora de todas as contas. O
        nome de cada ferramenta abre a página dela.
      </p>
      {/* `relative` prende aqui dentro os `sr-only` "(a melhor)" e "(a pior)":
          com posição absoluta e nenhum ancestral posicionado no meio, eles
          escapavam da rolagem e esticavam a página no celular — 628px de
          largura com quatro ferramentas, medido pela prova de tela. */}
      <div className="relative mt-3 overflow-x-auto">
        {/* As colunas têm LARGURA, e a tabela não estica: com uma ou duas
            ferramentas numa tabela de largura cheia, o número ia parar na borda
            direita do cartão, longe do rótulo, e o olho atravessava o cartão
            inteiro para ligar os dois. */}
        <table className="text-sm">
          <caption className="sr-only">
            As ferramentas escolhidas lado a lado, com a melhor e a pior de cada número marcadas
          </caption>
          <thead>
            <tr className="border-b border-borda text-xs text-cinza">
              {/* A coluna dos rótulos fica PRESA à esquerda: no celular a tabela
                  rola na horizontal, e sem isso quem rola perde de vista qual
                  número está lendo. */}
              {/* Mais estreita no celular: lá sobram uns 300px de cartão, e uma
                  coluna presa de 176px deixaria menos da metade para os números. */}
              <th scope="col" className="sticky left-0 z-[1] w-32 bg-superficie py-2 pr-4 text-left font-semibold sm:w-44">
                <span className="sr-only">Número</span>
              </th>
              {escolhidas.map(({ p, linha }) => (
                <th
                  key={linha.slug}
                  scope="col"
                  className="w-32 py-2 pl-3 text-right align-bottom font-semibold text-tinta sm:w-40"
                  data-lado-coluna={linha.slug}
                  data-posicao={p}
                >
                  {/* O nome quebra em duas linhas quando é longo ("Leitor
                      Inteligente de Processos"), e a chave da série fica na
                      altura da PRIMEIRA. */}
                  <span className="inline-flex items-start justify-end gap-1.5">
                    <span className="mt-px">
                      <ChaveDaSerie p={p} />
                    </span>
                    <Link
                      to={`/admin/avaliacoes/ferramentas?card=${encodeURIComponent(linha.slug)}${periodoNaBusca ? `&${periodoNaBusca}` : ""}`}
                      className="text-right text-link underline-offset-2 hover:underline"
                      data-abrir-ferramenta={linha.slug}
                    >
                      {linha.nome}
                    </Link>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {LINHAS_DO_LADO.map((r) => {
              const marcas = r.comparar ? destaquesDaLinha(linhas, r.comparar, r.menorEMelhor ?? false) : null;
              return (
                <tr key={r.chave} className="border-b border-borda/60 last:border-0" data-lado={r.chave}>
                  <th scope="row" className="sticky left-0 z-[1] bg-superficie py-2 pr-4 text-left align-top font-normal text-cinza">
                    {r.rotulo}
                    {r.explicacao && <span className="block text-xs">{r.explicacao}</span>}
                  </th>
                  {linhas.map((l) => {
                    const d = marcas?.get(l.slug) ?? null;
                    const v = r.comparar ? r.comparar(l) : null;
                    const cor =
                      d === "melhor"
                        ? "font-semibold text-aviso-tinta"
                        : d === "pior"
                          ? "font-semibold text-erro-tinta"
                          : r.forte
                            ? "text-titulo"
                            : "text-tinta";
                    return (
                      <td
                        key={l.slug}
                        className={`py-2 pl-3 text-right align-top tabular-nums ${r.forte ? "font-display text-base font-bold" : ""} ${cor}`}
                        data-lado-celula={`${r.chave}|${l.slug}`}
                        data-destaque={d ?? undefined}
                        data-valor={r.comparar ? (v ?? "") : undefined}
                      >
                        {d && (
                          <span aria-hidden="true" className="mr-1 text-[0.7em]">
                            {d === "melhor" ? "▲" : "▼"}
                          </span>
                        )}
                        <span data-lado-valor>{r.valor(l)}</span>
                        {d && <span className="sr-only">{d === "melhor" ? " (a melhor)" : " (a pior)"}</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 max-w-3xl space-y-2 text-xs leading-relaxed text-cinza" data-regra-do-destaque>
        <p>
          <strong className="font-semibold text-tinta">A melhor e a pior.</strong> Em cada número, a melhor das escolhidas
          fica verde, com ▲, e a pior fica vermelha, com ▼ — só entre as que têm avaliação no período, só quando elas são
          duas ou mais, e só quando os números diferem; num empate, as empatadas levam a mesma marca. Nas baixas, menor é
          melhor. A comparação é entre os números como aparecem aqui. Meta, situação, volume e data não têm marca: não são
          qualidade.
        </p>
        <p>
          <strong className="font-semibold text-tinta">NPS adaptado</strong> é o percentual de promotores (média 9 ou mais)
          menos o de detratores (média abaixo de 7), de −100 a 100. Não é o NPS das pesquisas, que pergunta de 0 a 10 se o
          aluno recomendaria o curso.
        </p>
      </div>
    </section>
  );
}
